/**
 * Conversation API client
 *
 * Provides methods for interacting with the Conversation Manager API.
 */

import { fetchApi, buildQueryString } from './client';
import type {
  ConversationFilters,
  ConversationListResponse,
  ConversationDetailResponse,
  TranscriptData,
} from '@/types';

// API endpoint for conversations - uses /api prefix to distinguish from frontend routes
const CONVERSATIONS_BASE = import.meta.env.VITE_CONVERSATION_API_BASE || '/api/conversations';

export async function listConversations(
  filters: ConversationFilters = {},
  pagination?: { pageSize?: number; pageToken?: string }
): Promise<ConversationListResponse> {
  const params: Record<string, string | number | boolean | undefined> = {
    topic: filters.topic,
    folder: filters.folder,
    search: filters.search,
    speaker: filters.speaker,
    created_after: filters.createdAfter || filters.dateFrom,
    created_before: filters.createdBefore || filters.dateTo,
    sort_by: filters.sortBy,
    sort_desc: filters.sortDesc,
    sort_order: filters.sortOrder,
    page_size: pagination?.pageSize,
    page_token: pagination?.pageToken,
  };

  if (filters.speakers?.length) {
    params.speakers = filters.speakers.join(',');
  }

  const query = buildQueryString(params);
  return fetchApi<ConversationListResponse>(`${CONVERSATIONS_BASE}${query}`);
}

export async function getConversation(
  conversationId: string,
  includeTranscript = false
): Promise<ConversationDetailResponse> {
  const query = includeTranscript ? '?include_transcript=true' : '';
  return fetchApi<ConversationDetailResponse>(
    `${CONVERSATIONS_BASE}/${conversationId}${query}`
  );
}

export async function getTranscript(conversationId: string): Promise<TranscriptData> {
  const response = await fetchApi<{ transcript: TranscriptData }>(
    `${CONVERSATIONS_BASE}/${conversationId}/transcript`
  );
  return response.transcript;
}

export async function countConversations(
  filters?: { topic?: string; folder?: string }
): Promise<{ count: number }> {
  const query = buildQueryString(filters || {});
  return fetchApi<{ count: number }>(`${CONVERSATIONS_BASE}/count${query}`);
}

export async function checkConversationExists(conversationId: string): Promise<boolean> {
  try {
    const response = await fetch(`${CONVERSATIONS_BASE}/${conversationId}`, {
      method: 'HEAD',
    });
    return response.ok;
  } catch {
    return false;
  }
}
